/**
 * ReportSolutions — Compact solution cards for the report overview.
 * Shows title, description snippet, evaluation tags or legacy score pills.
 */
import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Zap, Target, Lightbulb, BookOpen, ThumbsUp } from 'lucide-react';
import { EVALUATION_TAG_META } from '@kuutio/shared/evaluationTags.js';
import { Section, SectionTitle, Paragraph, METRIC_STYLES } from './reportStyles';
import SolutionAnalysis, { getCompositeScore, isTagBasedEvaluation } from './SolutionAnalysis';
import SolutionImplementationPlan from './SolutionImplementationPlan';
import SolutionBlockerAnswers from './SolutionBlockerAnswers';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const GROUP_COLORS = [
  '#367ABA', '#00613d', '#8b5cf6', '#fa6129',
  '#fa6129', '#06b6d4', '#ec4899', '#14b8a6',
];

const SolutionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const SolutionCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${p => p.$color};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const CardContent = styled.div`
  padding: 16px 20px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  flex: 1;
  line-height: 1.3;
`;

const TotalScore = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${p => p.$score >= 3 ? '#00613d' : '#fa6129'};
  white-space: nowrap;
  flex-shrink: 0;
  span { font-size: 12px; font-weight: 500; color: #9ca3af; }
`;

/* Thumbs-up count badge — surfaces the workshop vote that drives the
 * card order. Only shown when votes > 0 so unvoted instances stay clean. */
const VoteBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: #15803d;
  background: rgba(22, 163, 74, 0.12);
  border: 1px solid rgba(22, 163, 74, 0.35);
  padding: 4px 9px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 10px 0 0;
  ${p => p.$expanded ? `
    /* When the card is open the whole description should be visible — no
       line clamp, no ellipsis. */
    white-space: pre-wrap;
  ` : `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
`;

const ScorePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ScorePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 0;
  background: ${p => p.$bg || '#f3f4f6'};
  color: ${p => p.$color || '#6b7280'};
`;

const ReadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #367ABA;
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: inherit;
  &:hover { color: #2c5e91; background: rgba(54,122,186,0.06); }
  @media print { display: none; }
`;

const ExpandedDetail = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 20px;
  background: #fafbfc;
  @media print { display: none; }
`;

// Shared with ReportHybridSolutions / SolutionAnalysis via METRIC_STYLES.
const PILL_ICONS = { impact: Zap, feasibility: Target, innovation: Lightbulb, evidence: BookOpen };
const EVAL_PILL_STYLES = Object.fromEntries(
  Object.entries(METRIC_STYLES).map(([k, v]) => [k, { ...v, icon: PILL_ICONS[k], label: v.shortLabel }])
);

export default function ReportSolutions({ solutions = [], voteCounts = {}, summaryText, showImplementationPlans = true }) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);
  const [showAllUnvoted, setShowAllUnvoted] = useState(false);
  // Sort by thumbs-up vote count descending, falling back to order_index for
  // ties (and when no votes exist anywhere). This surfaces the most-validated
  // solutions first while keeping a stable display when nothing is voted.
  const sortedSolutions = useMemo(() => {
    const items = [...solutions];
    items.sort((a, b) => {
      const va = voteCounts[a.id] || 0;
      const vb = voteCounts[b.id] || 0;
      if (vb !== va) return vb - va;
      return (a.order_index ?? 999) - (b.order_index ?? 999);
    });
    return items;
  }, [solutions, voteCounts]);

  const hasAnyVotes = Object.values(voteCounts).some(n => n > 0);

  // When voting has happened, hide the zero-vote solutions behind an
  // expand button. The intent matches the Vipupisteet 'Näytä kaikki …'
  // pattern: the report's first impression should foreground the
  // workshop's validated picks. Without votes, this collapses to a
  // no-op and every solution renders as before.
  const unvotedCount = sortedSolutions.filter(s => !(voteCounts[s.id] > 0)).length;
  const visibleSolutions = (!hasAnyVotes || showAllUnvoted)
    ? sortedSolutions
    : sortedSolutions.filter(s => (voteCounts[s.id] || 0) > 0);

  if (!solutions.length) return null;

  return (
    <Section id="solutions">
      <SectionTitle>{t('solutionAnalysis.solutionsTitle')}</SectionTitle>
      <Paragraph>
        {summaryText || t('solutionAnalysis.solutionsIntro', { count: sortedSolutions.length })}
      </Paragraph>

      <SolutionGrid>
        {visibleSolutions.map((sol, i) => {
          const color = GROUP_COLORS[i % GROUP_COLORS.length];
          const compositeScore = getCompositeScore(sol.ai_analysis);
          const evaluation = sol.ai_analysis?.evaluation;
          const tagEval = isTagBasedEvaluation(evaluation);
          const title = sol.metadata?.ai_title || (() => {
            const t = sol.title || '';
            const first = t.split(/[.!?\n]/)[0] || t;
            return first.length > 80 ? first.substring(0, 77) + '...' : first;
          })();

          // Description snippet: use description or first 200 chars of title
          const snippet = sol.description || sol.title || '';

          const voteCount = voteCounts[sol.id] || 0;
          return (
            <SolutionCard key={sol.id || i} $color={color}>
              <CardContent>
                <CardTop>
                  <CardTitle>
                    {sol.id
                      ? <TranslatedText fieldKey={`solution.${sol.id}.title`} fallback={title} multiline={false} label="Ratkaisun otsikko" />
                      : title}
                  </CardTitle>
                  {voteCount > 0 && (
                    <VoteBadge title={t('report.solutions.voteTitle', { count: voteCount })}>
                      <ThumbsUp size={13} strokeWidth={2.4} />
                      {voteCount}
                    </VoteBadge>
                  )}
                  {compositeScore !== null && (
                    <TotalScore $score={compositeScore}>
                      {compositeScore} <span>/ 5</span>
                    </TotalScore>
                  )}
                </CardTop>

                <Description $expanded={expandedId === sol.id}>
                  {sol.id
                    ? <TranslatedText fieldKey={`solution.${sol.id}.description`} fallback={snippet} label="Ratkaisun kuvaus" />
                    : snippet}
                </Description>
                {expandedId !== sol.id && (
                  <SolutionBlockerAnswers
                    solutionId={sol.id}
                    narrative={sol.ai_analysis?.blockerNarrative}
                    variant="lead-in"
                  />
                )}

                <CardFooter>
                  <ScorePills>
                    {tagEval && evaluation.tags?.map((tag) => {
                      const meta = EVALUATION_TAG_META[tag.id] || { bgLight: '#f3f4f6', color: '#374151' };
                      const labelKey = `evaluationTags.${tag.id}`;
                      const label = t(labelKey);
                      const display = label === labelKey ? tag.id : label;
                      return (
                        <ScorePill key={tag.id + (tag.reasoning || '').slice(0, 8)} $bg={meta.bgLight} $color={meta.color}>
                          {display}
                        </ScorePill>
                      );
                    })}
                    {!tagEval && evaluation && Object.entries(EVAL_PILL_STYLES).map(([key, style]) => {
                      const data = evaluation[key];
                      if (!data?.score) return null;
                      const Icon = style.icon;
                      return (
                        <ScorePill key={key} $bg={style.bg} $color={style.color}>
                          <Icon size={11} />
                          {style.label} {data.score}
                        </ScorePill>
                      );
                    })}
                  </ScorePills>
                  {sol.id && (
                    <ReadMoreButton
                      type="button"
                      onClick={() => setExpandedId(expandedId === sol.id ? null : sol.id)}
                      aria-expanded={expandedId === sol.id}
                      aria-controls={`solution-detail-${sol.id}`}
                    >
                      {expandedId === sol.id ? <>{t('report.common.close')} <ChevronUp size={14} /></> : <>{t('report.common.readMore')} <ChevronDown size={14} /></>}
                    </ReadMoreButton>
                  )}
                </CardFooter>
              </CardContent>
              {expandedId === sol.id && (
                <ExpandedDetail id={`solution-detail-${sol.id}`}>
                  <SolutionBlockerAnswers
                    solutionId={sol.id}
                    narrative={sol.ai_analysis?.blockerNarrative}
                  />
                  <SolutionAnalysis analysis={sol.ai_analysis} solutionId={sol.id} />
                  {showImplementationPlans && (
                    <SolutionImplementationPlan plan={sol.ai_analysis?.implementationPlan} solutionId={sol.id} />
                  )}
                </ExpandedDetail>
              )}
            </SolutionCard>
          );
        })}
      </SolutionGrid>
      {hasAnyVotes && !showAllUnvoted && unvotedCount > 0 && (
        <button
          onClick={() => setShowAllUnvoted(true)}
          style={{
            display: 'block', margin: '16px auto', padding: '8px 24px',
            background: 'none', border: '1px solid #d1d5db', borderRadius: 8,
            color: '#6b7280', fontSize: '0.9rem', cursor: 'pointer',
          }}
        >
          {t('report.solutions.showUnvoted')}
        </button>
      )}
    </Section>
  );
}
